import { DatapointsClimateChartService } from "src/app/dataset/datapoints/climate-charts/datapoints-climate-chart.service";
import jsPDF from "jspdf";
import { NotifService } from "../notification/notif.service";
import { LocationProfile } from "src/app/model/datapoint/location-profile/location-profile";

export class Climate {

    public static async downloadOverlayProfileChart(datapointsClimateChartService: DatapointsClimateChartService, notifService: NotifService, fileName: string, datapointSummaryFields = null, addressDetails = null, selectedValue = null) {
        const allGraphTypes = datapointsClimateChartService.chartGraphicsConfiguration.graphType;
        
        // Define CLIMATE-specific chart types
        const climateChartTypes = ['maxTemperature', 'meanTemperature', 'minTemperature', 'snowfallTemperature', 'precipitation', 'surfaceWind', 'populationDensity', 'seaLevelRise', 'seaLevelRiseWarming'];
        
        // Filter graphTypes based on selectedValue
        let graphTypes: any;
        if (selectedValue === new LocationProfile().CLIMATE) {
            // Use only CLIMATE-specific chart types
            graphTypes = {};
            climateChartTypes.forEach(key => {
                if (allGraphTypes[key]) {
                    graphTypes[key] = allGraphTypes[key];
                }
            });
        } else {
            // Use all remaining chart types (exclude CLIMATE-specific ones)
            graphTypes = {};
            for (const key in allGraphTypes) {
                if (Object.prototype.hasOwnProperty.call(allGraphTypes, key) && !climateChartTypes.includes(key)) {
                    graphTypes[key] = allGraphTypes[key];
                }
            }
        }
        
        // The Region tab's charts belong to Climate Change and go after its other charts.
        const regionGraphs = (selectedValue === new LocationProfile().CLIMATE)
            ? []
            : (datapointsClimateChartService.regionGraphs || []).filter(graph => graph?.instance);

        // Consolidated risk score charts are per vulnerability profile type, so they live in
        // their own list rather than in graphType. They belong to the Climate Change section,
        // and go at the end of the same document - never a separate one.
        const consolidatedRiskScoreGraphs = (selectedValue === new LocationProfile().CLIMATE)
            ? []
            : (datapointsClimateChartService.consolidatedRiskScoreGraphs || []).filter(graph => graph?.instance);

        // Check if any chart instances are initialized (check ALL charts, not just filtered ones)
        const hasInitializedCharts = Object.keys(allGraphTypes).some(key => allGraphTypes[key]?.instance)
            || regionGraphs.length > 0
            || consolidatedRiskScoreGraphs.length > 0;

        if (!hasInitializedCharts) {
            notifService.error('Charts are still loading. Please wait for the charts to fully render before downloading.');
            return;
        }

        // Check if the filtered graphTypes have any initialized charts
        const hasFilteredCharts = Object.keys(graphTypes).some(key => graphTypes[key]?.instance)
            || regionGraphs.length > 0
            || consolidatedRiskScoreGraphs.length > 0;

        if (!hasFilteredCharts) {
            notifService.error('No charts available for the selected tab. Please switch to the correct tab or wait for charts to load.');
            return;
        }
        
        const format = datapointsClimateChartService.chartGraphicsConfiguration.format;
        let options = {orientation: 'p', unit: 'mm'};
        const doc = new jsPDF(options);

        const bioclimaticRegion = datapointsClimateChartService.bioclimaticRegion;

        let tableHeight = 24;
        if(datapointSummaryFields){
            if (bioclimaticRegion) {
                datapointSummaryFields = [...datapointSummaryFields,
                    {name: 'Continent', value: bioclimaticRegion.continent || ''},
                    {name: 'Region', value: bioclimaticRegion.region || ''},
                    {name: 'Region Acronym', value: bioclimaticRegion.acronym || ''}];
            }
            this.createHeading(doc, 'Location Profile', 14)
            tableHeight = 40;
            const margin = 10;
            const startY = 30;
            const cellWidth = 60;
            const cellHeight = 5;

            datapointSummaryFields.forEach((summaryField, i) => {
                const resultArray = Object.values(summaryField);
                for (let j = 0; j < resultArray.length; j++) {
                    const cell = resultArray[j];
                    this.createCell(doc, cell, {x: (margin + j * cellWidth), y: (startY + i * cellHeight), width: cellWidth, height: cellHeight})
                }

                tableHeight = tableHeight + cellHeight;
            });
        }

        if(addressDetails){
            const startY = 29;
            const cellHeight = 5;
            this.createHeading(doc, 'Address Profile', 14)
            this.createHeading(doc, 'Address', 10, {x: 10, y: 27})
            this.createCell(doc, addressDetails.address || "", {x: 10, y: startY + cellHeight, width: 120, height: cellHeight});
            this.createHeading(doc, 'Coordinates (latitude, longitude)', 10, {x: 10, y: 42});
            this.createCell(doc, addressDetails.yAxis || "", {x: 10, y: 49, width: 60, height: cellHeight});
            this.createCell(doc, addressDetails.xAxis || "", {x: 70, y: 49, width: 60, height: cellHeight});
            if (bioclimaticRegion) {
                this.createHeading(doc, 'Continent', 10, {x: 10, y: 57});
                this.createHeading(doc, 'Region', 10, {x: 70, y: 57});
                this.createHeading(doc, 'Region Acronym', 10, {x: 130, y: 57});
                this.createCell(doc, bioclimaticRegion.continent || "", {x: 10, y: 64, width: 60, height: cellHeight});
                this.createCell(doc, bioclimaticRegion.region || "", {x: 70, y: 64, width: 60, height: cellHeight});
                this.createCell(doc, bioclimaticRegion.acronym || "", {x: 130, y: 64, width: 60, height: cellHeight});
            }
        }

        doc.setFont("arial", "bold");
        doc.setFontSize(14);
        let counter = 1;
        // jsPDF reuses an image added under an alias it has seen, so each chart needs its own -
        // counter does not advance past the first page's chart and would repeat its picture.
        let imageCount = 0;
        let isAddPage: boolean = false;
        let isFirstPage = true;
        let skippedCharts = 0;

        // Helper function to load image asynchronously
        const loadImage = (dataUrl: string): Promise<HTMLImageElement> => {
            return new Promise((resolve, reject) => {
                const img = new Image();
                img.onload = () => resolve(img);
                img.onerror = (error) => reject(new Error('Failed to load chart image'));
                img.src = dataUrl;
            });
        };

        // Adds one chart to the document, keeping the running page/slot state - shared so the
        // Region and consolidated risk score charts land in the same document, laid out identically.
        const addChart = async (element: {title: string, instance: any}, key: string): Promise<void> => {
            try {
                const dataUrl = element.instance.getDataURL({
                    pixelRatio: 2,
                    backgroundColor: '#fff'
                });

                // Validate data URL before attempting to load
                if (!dataUrl || dataUrl.length < 100 || !dataUrl.startsWith('data:image/')) {
                    console.warn(`Skipping chart ${key}: Invalid or empty data URL`);
                    skippedCharts++;
                    return;
                }

                // Wait for image to load completely
                const img = await loadImage(dataUrl);

                if(isFirstPage){
                    if(datapointSummaryFields){
                        this.createChart(doc, ++imageCount, img, format, element, {x: 10, y: tableHeight + 30})
                    }
                    if(addressDetails){
                        this.createChart(doc, ++imageCount, img, format, element, {x: 10, y: 100})
                    }

                } else {
                    if(counter === 1){
                        doc.addPage("landscape");
                    }
                    if (counter % 2 !== 0) {
                        if (isAddPage) {
                            doc.addPage("landscape");
                            isAddPage = false;
                        }
                        this.createChart(doc, ++imageCount, img, format, element, {x: 10, y: 24})
                    } else {
                        this.createChart(doc, ++imageCount, img, format, element, {x: 10, y: 144})
                        isAddPage = true;
                    }

                }
                if(!isFirstPage){
                    counter++;
                }
                isFirstPage = false;
            } catch (error) {
                console.warn(`Skipping chart ${key}: ${error.message || 'Failed to load'}`);
                skippedCharts++;
                // Continue with other charts even if one fails
            }
        };

        // Process charts sequentially to ensure proper loading
        for (const key in graphTypes) {
            if (Object.prototype.hasOwnProperty.call(graphTypes, key)) {
                const element = graphTypes[key];

                if (element.instance) {
                    await addChart(element, key);
                }
            }
        }

        for (const graph of regionGraphs) {
            // The PDF heading spells out what the graph is; the panel's title is shorter.
            await addChart({ ...graph, title: `Region for ${graph.title} in Score` }, graph.title);
        }

        // Then the consolidated risk score charts, one per vulnerability profile type.
        for (const graph of consolidatedRiskScoreGraphs) {
            // The PDF heading spells out what the graph is; the panel's title is shorter.
            const heading = graph.title.startsWith('Instant Calculation')
                ? `Instant Calculated Consolidated Risk Score for ${graph.title.replace(/^Instant Calculation\s*/, '')}`
                : `Consolidated Risk Score for ${graph.title}`;
            await addChart({ ...graph, title: heading }, graph.title);
        }
        if (counter > 1 || !isFirstPage) {
            doc.save(fileName + ".pdf");
            if (skippedCharts > 0) {
                notifService.success(`Chart downloaded successfully.`);
            } else {
                notifService.success('Chart downloaded successfully.');
            }
        } else {
            notifService.error('No chart data available to download. Please ensure the charts are fully loaded.');
        }
    }

    public static resetGraphTypes(datapointsClimateChartService: DatapointsClimateChartService) {
        const graphTypes = datapointsClimateChartService.chartGraphicsConfiguration.graphType;
        for (const key in graphTypes) {
            if (Object.prototype.hasOwnProperty.call(graphTypes, key)) {
                const element = graphTypes[key];
                element.instance = null;
            }
        }
        // Rebuilt per location, so their instances go stale with the rest.
        datapointsClimateChartService.regionGraphs = [];
        // The consolidated risk score charts are rebuilt per location, so their instances go
        // stale with the rest - leaving them would export a previous location's charts.
        datapointsClimateChartService.consolidatedRiskScoreGraphs = [];
    }

    public static createHeading(doc, heading, fontSize, dimensions = {x: 10, y: 20}){
        doc.setFont("arial", "bold");
        doc.setFontSize(fontSize);
        doc.text(dimensions.x, dimensions.y, heading);
        doc.setFont("Helvetica", "normal");
        doc.setFontSize(10);
    }

    public static createCell(doc, text, dimensions = {x: 10, y: 20, width: 60, height: 5}){
        doc.rect(dimensions.x,  (dimensions.y) - 4, dimensions.width, dimensions.height);
        doc.text((dimensions.x) + 2, dimensions.y, text);
    }

    public static createChart(doc, imageIndex, img, format, element, dimensions = {x: 10, y: 20}){
        doc.setFont("arial", "bold");
        doc.setFontSize(12);
        doc.text(dimensions.x + 2, dimensions.y - 6, element.title);
        doc.setFont("Helvetica", "normal");
        doc.setFontSize(10);
        doc.rect(dimensions.x, dimensions.y , 170, 100);
        doc.addImage(img, format, 15, dimensions.y + 5, 150, 90, "alias"+imageIndex, 'SLOW');
    }
}
